/**
 * A stored sub-question keeps its own option list under `children`, but the
 * questionnaire renderer reads sub-question options from `options`.
 * Without this, a nested select/multi_select renders with no options.
 */
const mapOptionChildToSubQuestion = (child) => {
    if (!child || typeof child !== 'object') {
        return child;
    }

    const nestedOptions = Array.isArray(child.options) && child.options.length > 0
        ? child.options
        : (Array.isArray(child.children) ? child.children : []);

    return {
        ...child,
        options: nestedOptions,
        settings: child.settings || {}
    };
}

const mapOptionWithStoredChildren = (option) => {
    if (!Array.isArray(option?.children) || option.children.length === 0) {
        return {...option};
    }

    return {
        ...option,
        children: option.children.map(mapOptionChildToSubQuestion)
    };
}

export const mapQuestions = (questions, answers = []) => {
    const result = questions.map((question) => {
        let options = [];

        const questionSettings = question.question_setting;

        if (question.type === 'radiobox') {
            options = (question?.options || []).map((option) => {
                const children = [];

                // Legacy follow-up: the field type lives on `option.type` and
                // `children` holds that field's option entries, not sub-questions.
                if(option.type !== 'radiobox') {
                    const childOption = {
                        id: `${option.id}-1`,
                        title: option.extended_title,
                        type: option.type,
                        options: (option.children || []),
                        settings: {}
                    }

                    if(option.type === 'date' && questionSettings === 'max_date_now'){
                        childOption.settings.maxSelectableDate = new Date().toISOString().split('T')[0]
                    }

                    children.push(childOption)

                    return {
                        ...option,
                        children,
                        settings: {},
                        type: 'radiobox'
                    }
                }
                else {
                    return mapOptionWithStoredChildren(option)
                }
            });
        }
        else{
            options = (question.options || []).map(mapOptionWithStoredChildren);
        }

        const prevAnswer = answers.find((ans) => ans.id === question.id)

        return {
            ...question,
            options: options,
            is_ai_filled: !!prevAnswer?.is_ai_filled,
            settings: {}
        };
    })

    return result;
}

export const mapAnswers = (questions) => {
    return questions.map((question) => {
        return {
            ...question,
            answers: (question.answers || []).filter((answer) => {
                return (!('value' in answer) && !('title' in answer)) ? false : true;
            }).map((answer) => {
                return {
                    ...answer,
                    children: (answer.children || []).map((child) => {
                        return {
                            ...child,
                            id: child.value || child.id
                        }
                    })
                }
            })
        }
    });
}

export const mapAnswersToSave = (questions, answers) => {
    return answers.map((answer) => {
        const relatedQuestion = questions.find((q) => q.id === answer.question)

        switch(relatedQuestion?.type){
            case 'file_upload':
                return {
                    ...answer,
                    answer: (answer.answer || []).map((file) => {
                        return {
                            name: file.name,
                            title: file.title,
                            file_size: file.attachmentSize
                        }
                    })
                }
            default:
                return answer
        }
    })
} 